import { CoralogixRum } from "@coralogix/browser";
import { dmnIdentity } from "../utils/dmnIdentity";
import {
  getEnvironmentProps,
  isBrowser,
  isLocalDev
} from "../utils/environment";

const CORALOGIX_PUBLIC_KEY = "cxtp_XKePvRJwFB3BqOldOMPkBRYwEpdDdW";
const SUBSYSTEM_NAME = isBrowser() ? "CollinsWeb2.0" : "CollinsMobile";

export const mapEnvName = (env: string): string => {
  switch (env.toLowerCase()) {
    case "development":
      return "Dev";
    case "production":
      return "Production";
    case "pre-prod":
      return "PreProd";
    case "staging":
      return "Staging";
    case "demo":
      return "Demo";
    default:
      return env;
  }
};

let initialised = false;

/**
 * Resolve the Coralogix `application` field.
 *
 * The application name includes the detected environment so that
 * source-map uploads (done only for Production in the release pipeline)
 * match the runtime application name exactly.
 */
async function resolveApplicationContext(): Promise<{
  application: string;
  environment: string;
}> {
  const token = await dmnIdentity.getAccessToken();
  const env = getEnvironmentProps(token);
  const environment = mapEnvName(env.ENVIRONMENT ?? "development");
  return { application: `${SUBSYSTEM_NAME}.${environment}`, environment };
}

/**
 * Initialise the Coralogix RUM SDK.
 *
 * Call this once, as early as possible (before React renders) so that
 * automatic instrumentation captures everything from the start.
 */
export async function initialiseTelemetry(): Promise<void> {
  if (initialised) return;

  try {
    const { application, environment } = await resolveApplicationContext();
    const version = process.env.BUILD_BUILDNUMBER ?? "development";

    CoralogixRum.init({
      public_key: CORALOGIX_PUBLIC_KEY,
      application,
      version,
      coralogixDomain: "EU2",
      environment,
      sessionRecordingConfig: isLocalDev()
        ? undefined
        : {
            enable: true,
            autoStartSessionRecording: true
          }
    });

    initialised = true;
  } catch (err) {
    console.error("[Telemetry] Failed to initialise Coralogix RUM SDK:", err);
  }
}

/**
 * Report an error to the Coralogix telemetry backend.
 *
 * Uses `CoralogixRum.error()` for log-based error reporting and
 * `CoralogixRum.captureError()` for structured error capture with
 * additional metadata.
 */
export function reportTelemetryError(
  error: Error,
  attributes?: Record<string, string>
): void {
  if (isLocalDev()) {
    // Avoid reporting errors in local development to prevent cluttering Coralogix with non-actionable data
    console.warn(
      "[Telemetry] Skipping error report in local development:",
      error
    );
    return;
  }

  try {
    const labels = attributes?.screenshotId
      ? { screenshotId: attributes.screenshotId }
      : undefined;
    CoralogixRum.captureError(error, attributes, labels);
  } catch (err) {
    console.error("[Telemetry] Failed to report error:", err);
  }
}

export function captureScreenshot(description?: string): string | undefined {
  if (isLocalDev()) {
    // Avoid capturing screenshots in local development to prevent cluttering Coralogix with non-actionable data
    return undefined;
  }

  try {
    return CoralogixRum.screenshot(description);
  } catch (err) {
    console.error("[Telemetry] Failed to capture screenshot:", err);
    return undefined;
  }
}

/**
 * Set user context on the Coralogix RUM session.
 *
 * This associates all subsequent events with the given user, replacing
 * the previous `setTelemetryAttribute` approach.
 */
export function setUserContext(
  userId: string,
  userName: string,
  userEmail?: string,
  metadata?: Record<string, string>
): void {
  try {
    CoralogixRum.setUserContext({
      user_id: userId,
      user_name: userName,
      user_email: userEmail,
      user_metadata: metadata
    });
  } catch (err) {
    console.error("[Telemetry] Failed to set user context:", err);
  }
}

/**
 * Set labels on the Coralogix RUM session.
 *
 * Labels are key/value pairs attached to every subsequent event.
 * This replaces the previous `setTelemetryAttribute` approach for
 * non-user metadata (venue, app version, viewport, etc.).
 */
export function setTelemetryLabels(labels: Record<string, string>): void {
  try {
    CoralogixRum.setLabels(labels);
  } catch (err) {
    console.error("[Telemetry] Failed to set labels:", err);
  }
}
